'use client'

// React
import { useRef, useState, useEffect } from 'react'

// Next.js
import Link from 'next/link'

// Utils
import { Join } from '../../lib/utils'

interface ButtonFXProps {
  href: string
  children: React.ReactNode
  icon?: React.ReactNode
}

export function ButtonFX({ href, children, icon }: ButtonFXProps) {
  const buttonRef = useRef<HTMLAnchorElement>(null)
  const [rotation, setRotation] = useState(0)

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!buttonRef.current) return

      const rect = buttonRef.current.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2

      const deltaX = e.clientX - centerX
      const deltaY = e.clientY - centerY

      // Calculate angle in degrees
      const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI)
      setRotation(angle)
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  return (
    <Link
      ref={buttonRef}
      href={href}
      className="group relative inline-flex max-w-max min-w-max items-center gap-2 overflow-hidden rounded-full px-6 py-3 text-center font-medium transition-all duration-300 hover:scale-105"
    >
      {/* Animated gradient border */}
      <div
        className={Join(
          'absolute inset-0 rounded-full bg-linear-to-r transition-all duration-300',
          'from-blue-600 via-blue-400 to-blue-500',
          'dark:from-teal-600 dark:via-teal-200 dark:to-teal-500',
          // 'dark:from-orange-300 dark:via-orange-200 dark:to-orange-400',
          'dark:group-hover:from-emerald-300 dark:group-hover:via-emerald-100 dark:group-hover:to-emerald-300',
          'blur-sm group-hover:blur-xl',
        )}
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: 'transform 0.5s ease-out',
        }}
      />

      {/* Glass background */}
      <div
        className={Join(
          'absolute inset-[2px] rounded-full bg-linear-to-r backdrop-blur-xl transition-colors',
          'from-blue-500 via-blue-500 to-blue-500',
          'dark:from-teal-600 dark:via-teal-500 dark:to-teal-600',
          // 'dark:from-[#CF5605] dark:via-[#D18603] dark:to-[#D39000]',
          'hover:from-blue-600 hover:via-blue-400 hover:to-blue-500',
          'dark:group-hover:from-emerald-500 dark:group-hover:via-emerald-500 dark:group-hover:to-emerald-500',
        )}
      />

      {/* Content */}
      <span className="pointer-events-none relative z-10 flex items-center gap-2 text-white drop-shadow-lg group-hover:text-white">
        {icon && <>{icon}</>}
        {children}
      </span>
    </Link>
  )
}
